import { DI } from '@intigral/constant/config/DIContainer';
import { IGetBecauseYouWatchedMultipleUseCase } from '../../core/usecases/thinkAnalytics/getBecauseYouWatchedMultiple';
import { IGetRelatedUseCase } from '../../core/usecases/thinkAnalytics/getRelated';
import { IGetRelatedOthersUseCase } from '../../core/usecases/thinkAnalytics/getRelatedOthers';
import { IGetTAFeedUseCase } from '../../core/usecases/thinkAnalytics/getTAFeedUseCase';
import { IGetFeelingLuckyUseCase } from '../../core/usecases/thinkAnalytics/GetFeelingLuckyUseCase';
import { IGetMoodVodUseCase } from '../../core/usecases/thinkAnalytics/GetMoodVodUseCase';
import { IGetZoneUseCase } from '../../core/usecases/thinkAnalytics/GetZoneUseCase';
import { IGetVodListAndYouWatchedUseCase } from '../../core/usecases/thinkAnalytics/getVodListAndYouWatched';
import { container } from '../../DataDomain';
import {
    IThinkAnalyticsGetFeelingLucky,
    IThinkAnalyticsgetMoodVod,
    IThinkAnalyticsGetMoreLikeThisResponse,
    IThinkAnalyticsGetZone,
} from '../../entity/interface/iThinkAnalytics';

export interface IThinkAnalyticsService {
    getVodListAndYouWatched(
        apiKey: string,
        token: string,
        queryParams: any
    ): Promise<IThinkAnalyticsGetMoreLikeThisResponse>;

    getRelated(apiKey: string, token: string, queryParams: any): Promise<IThinkAnalyticsGetMoreLikeThisResponse>;

    getRelatedOthers(apiKey: string, token: string, queryParams: any): Promise<IThinkAnalyticsGetMoreLikeThisResponse>;

    getBecauseYouWatchedMultiple(
        apiKey: string,
        token: string,
        queryParams: any
    ): Promise<IThinkAnalyticsGetMoreLikeThisResponse>;

    getTAByUsecaseId(
        datasource: string,
        apiKey: string,
        token: string,
        queryParams: any
    ): Promise<IThinkAnalyticsGetMoreLikeThisResponse>;

    getFeelingLucky(
        apiKey: string,
        token: string,
        profileName: string,
        userName: string,
        device: string,
        country: string,
        subGuids: string,
        rating: string,
        isKidsMode: boolean
    ): Promise<IThinkAnalyticsGetFeelingLucky>;

    getMoodVod(
        apiKey: string,
        token: string,
        profileName: string,
        userName: string,
        device: string,
        country: string,
        subGuids: string,
        rating: string,
        useCaseId: string
    ): Promise<IThinkAnalyticsgetMoodVod>;

    getZone(
        url: string,
        token: string,
        apiKey: string,
        profileName: string,
        userName: string
    ): Promise<IThinkAnalyticsGetZone>;
}

export class ThinkAnalyticsService implements IThinkAnalyticsService {
    // Lazy getters for dependencies to avoid circular dependencies
    private get getVodListAndYouWatchedUseCase(): IGetVodListAndYouWatchedUseCase {
        return container.resolve<IGetVodListAndYouWatchedUseCase>(DI.USECASES.IGetVodListAndYouWatchedUseCase);
    }

    private get getRelatedUseCase(): IGetRelatedUseCase {
        return container.resolve<IGetRelatedUseCase>(DI.USECASES.IGetRelatedUseCase);
    }

    private get getRelatedOthersUseCase(): IGetRelatedOthersUseCase {
        return container.resolve<IGetRelatedOthersUseCase>(DI.USECASES.IGetRelatedOthersUseCase);
    }

    private get getBecauseYouWatchedMultipleUseCase(): IGetBecauseYouWatchedMultipleUseCase {
        return container.resolve<IGetBecauseYouWatchedMultipleUseCase>(
            DI.USECASES.IGetBecauseYouWatchedMultipleUseCase
        );
    }

    private get getTAFeedUseCase(): IGetTAFeedUseCase {
        return container.resolve<IGetTAFeedUseCase>(DI.USECASES.IGetTAFeedUseCase);
    }

    private get getFeelingLuckyUseCase(): IGetFeelingLuckyUseCase {
        return container.resolve<IGetFeelingLuckyUseCase>(DI.USECASES.IGetFeelingLuckyUseCase);
    }

    private get getMoodVodUseCase(): IGetMoodVodUseCase {
        return container.resolve<IGetMoodVodUseCase>(DI.USECASES.IGetMoodVodUseCase);
    }

    private get getZoneUseCase(): IGetZoneUseCase {
        return container.resolve<IGetZoneUseCase>(DI.USECASES.IGetZoneUseCase);
    }

    constructor() {
        // No initialization needed - dependencies resolved lazily via getters
    }

    async getVodListAndYouWatched(
        apiKey: string,
        token: string,
        queryParams: any
    ): Promise<IThinkAnalyticsGetMoreLikeThisResponse> {
        const response = await this.getVodListAndYouWatchedUseCase.execute(apiKey, token, queryParams);
        return response;
    }

    async getRelated(apiKey: string, token: string, queryParams: any): Promise<IThinkAnalyticsGetMoreLikeThisResponse> {
        const response = await this.getRelatedUseCase.execute(apiKey, token, queryParams);
        return response;
    }

    async getRelatedOthers(
        apiKey: string,
        token: string,
        queryParams: any
    ): Promise<IThinkAnalyticsGetMoreLikeThisResponse> {
        const response = await this.getRelatedOthersUseCase.execute(apiKey, token, queryParams);
        return response;
    }

    async getBecauseYouWatchedMultiple(
        apiKey: string,
        token: string,
        queryParams: any
    ): Promise<IThinkAnalyticsGetMoreLikeThisResponse> {
        const response = await this.getBecauseYouWatchedMultipleUseCase.execute(apiKey, token, queryParams);
        return response;
    }

    async getTAByUsecaseId(
        datasource: string,
        apiKey: string,
        token: string,
        queryParams: any
    ): Promise<IThinkAnalyticsGetMoreLikeThisResponse> {
        const response = await this.getTAFeedUseCase.execute(datasource, apiKey, token, queryParams);
        return response;
    }

    async getFeelingLucky(
        apiKey: string,
        token: string,
        profileName: string,
        userName: string,
        device: string,
        country: string,
        subGuids: string,
        rating: string,
        isKidsMode: boolean
    ): Promise<IThinkAnalyticsGetFeelingLucky> {
        const response = await this.getFeelingLuckyUseCase.execute(
            apiKey,
            token,
            profileName,
            userName,
            device,
            country,
            subGuids,
            rating,
            isKidsMode
        );
        return response;
    }

    async getMoodVod(
        apiKey: string,
        token: string,
        profileName: string,
        userName: string,
        device: string,
        country: string,
        subGuids: string,
        rating: string,
        useCaseId: string
    ): Promise<IThinkAnalyticsgetMoodVod> {
        const response = await this.getMoodVodUseCase.execute(
            apiKey,
            token,
            profileName,
            userName,
            device,
            country,
            subGuids,
            rating,
            useCaseId
        );
        return response;
    }

    async getZone(
        url: string,
        token: string,
        apiKey: string,
        profileName: string,
        userName: string
    ): Promise<IThinkAnalyticsGetZone> {
        const response = await this.getZoneUseCase.execute(url, token, apiKey, profileName, userName);

        return response;
    }
}
